import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

const COST_ENGAGEMENT = 0.50;
const COST_CLICK = 0.60;
const WEEKLY_BUDGET = 100;

function getLast12HourBuckets() {
  const now = new Date();
  const currentHour = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours()
  ));
  return Array.from({ length: 12 }, (_, i) =>
    new Date(currentHour.getTime() - (11 - i) * 3600000)
  );
}

function bucketKey(utcDate) {
  return utcDate.getTime().toString();
}

function fmtHourLocal(utcDate) {
  return utcDate.toLocaleTimeString([], { hour: 'numeric', hour12: true });
}

function fmtTime(seconds) {
  if (!seconds || seconds < 1) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function isThisWeek(isoString) {
  if (!isoString) return false;
  const d = new Date(isoString);
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 3600000);
  return d >= weekAgo;
}

// ── sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, color = '#1c1c1e' }) => (
  <div style={{
    background: '#fff',
    borderRadius: 16,
    padding: '20px 24px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    flex: 1,
    minWidth: 130,
  }}>
    <div style={{ fontSize: 11, color: '#8E8E93', fontWeight: 700, letterSpacing: 0.5, marginBottom: 8, textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{ fontSize: 32, fontWeight: 700, color, lineHeight: 1, marginBottom: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#8E8E93', marginTop: 4 }}>{sub}</div>}
  </div>
);

const HBar = ({ label, value, max, color = '#2D8C4E', pct, hideValue }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13, marginBottom: 5 }}>
      <span style={{ color: '#1c1c1e', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: hideValue ? '100%' : '72%' }}>
        {label}
      </span>
      {!hideValue && (
        <span style={{ color: '#8E8E93', fontSize: 12, flexShrink: 0 }}>
          {value}{pct ? ` (${Math.round((value / max) * 100)}%)` : ''}
        </span>
      )}
    </div>
    <div style={{ background: '#f2f2f7', borderRadius: 4, height: 5, overflow: 'hidden' }}>
      <div style={{
        width: `${max > 0 ? Math.max((value / max) * 100, 2) : 0}%`,
        background: color,
        height: '100%',
        borderRadius: 4,
        transition: 'width 0.7s cubic-bezier(0.4,0,0.2,1)',
      }} />
    </div>
  </div>
);

const Card = ({ title, children, style = {} }) => (
  <div style={{
    background: '#fff',
    borderRadius: 16,
    padding: '20px 24px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    ...style,
  }}>
    {title && (
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 18 }}>
        {title}
      </div>
    )}
    {children}
  </div>
);

const LineChart = ({ vals, labels }) => {
  const W = 700, H = 140;
  const PAD = { top: 12, right: 12, bottom: 28, left: 24 };
  const iW = W - PAD.left - PAD.right;
  const iH = H - PAD.top - PAD.bottom;
  const maxVal = Math.max(...vals, 1);
  const total = vals.reduce((a, b) => a + b, 0);

  if (total === 0) {
    return (
      <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8E8E93', fontSize: 13 }}>
        No impressions in the last 12 hours
      </div>
    );
  }

  const pts = vals.map((v, i) => ({
    x: PAD.left + (vals.length === 1 ? iW / 2 : (i / (vals.length - 1)) * iW),
    y: PAD.top + iH - (v / maxVal) * iH,
    v,
  }));

  const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaD = `${pathD} L${pts[pts.length - 1].x.toFixed(1)} ${(PAD.top + iH).toFixed(1)} L${pts[0].x.toFixed(1)} ${(PAD.top + iH).toFixed(1)} Z`;
  const showEvery = Math.ceil(labels.length / 6);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {[0, 0.5, 1].map((t, i) => (
        <line key={i} x1={PAD.left} y1={PAD.top + iH * (1 - t)} x2={PAD.left + iW} y2={PAD.top + iH * (1 - t)} stroke="#f2f2f7" strokeWidth={1} />
      ))}
      <path d={areaD} fill="rgba(45,140,78,0.06)" />
      <path d={pathD} fill="none" stroke="#2D8C4E" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
      {pts.map((p, i) => p.v > 0 && (
        <circle key={i} cx={p.x} cy={p.y} r={3.5} fill="#fff" stroke="#2D8C4E" strokeWidth={2} />
      ))}
      {labels.map((lbl, i) => i % showEvery === 0 && (
        <text key={i}
          x={PAD.left + (labels.length === 1 ? iW / 2 : (i / (labels.length - 1)) * iW)}
          y={H - 6}
          textAnchor="middle"
          fontSize={9}
          fill="#8E8E93"
          fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
        >
          {lbl}
        </text>
      ))}
    </svg>
  );
};

const BudgetCard = ({ weeklyEngagements, weeklyClicks }) => {
  const engagementSpend = weeklyEngagements * COST_ENGAGEMENT;
  const clickSpend = weeklyClicks * COST_CLICK;
  const totalSpend = engagementSpend + clickSpend;
  const remaining = Math.max(WEEKLY_BUDGET - totalSpend, 0);
  const pctEngagement = Math.min((engagementSpend / WEEKLY_BUDGET) * 100, 100);
  const pctClick = Math.min((clickSpend / WEEKLY_BUDGET) * 100, 100 - pctEngagement);
  const overBudget = totalSpend > WEEKLY_BUDGET;
  const fmt = n => `$${n.toFixed(2)}`;

  return (
    <Card>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
        Weekly Budget
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 18 }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: '#1c1c1e' }}>{fmt(totalSpend)}</span>
        <span style={{ fontSize: 13, color: '#8E8E93' }}>of {fmt(WEEKLY_BUDGET)} spent</span>
      </div>

      <div style={{ background: '#f2f2f7', borderRadius: 8, height: 10, overflow: 'hidden', display: 'flex', marginBottom: 10 }}>
        <div style={{ width: `${pctEngagement}%`, background: '#2D8C4E', transition: 'width 0.8s ease' }} />
        <div style={{ width: `${pctClick}%`, background: '#F39C12', transition: 'width 0.8s ease' }} />
        <div style={{ flex: 1, background: 'transparent' }} />
      </div>

      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#8E8E93', marginBottom: 20 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: '#2D8C4E', display: 'inline-block' }} />
          Engagements
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: '#F39C12', display: 'inline-block' }} />
          Clicks
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: '#f2f2f7', border: '1px solid #e5e5ea', display: 'inline-block' }} />
          Remaining
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#636366' }}>{weeklyEngagements} engagements × $0.50</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#2D8C4E' }}>{fmt(engagementSpend)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#636366' }}>{weeklyClicks} clicks × $0.60</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#F39C12' }}>{fmt(clickSpend)}</span>
        </div>
        <div style={{ height: 1, background: '#f2f2f7', margin: '2px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Total Spent</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: overBudget ? '#FF3B30' : '#1c1c1e' }}>{fmt(totalSpend)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Remaining</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#2D8C4E' }}>{fmt(remaining)}</span>
        </div>
      </div>

      {overBudget && (
        <div style={{ marginTop: 14, background: '#fff2f2', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: '#FF3B30', textAlign: 'center', fontWeight: 600 }}>
          Weekly budget exceeded
        </div>
      )}
    </Card>
  );
};

// ── main ──────────────────────────────────────────────────────────────────────

const LandeedDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    const { data: rows, error } = await supabase
      .from('ad_impressions')
      .select('impression_at, clicked, feedback, time_on_screen_seconds, device_type, country, demographic_data')
      .eq('advertiser', 'landeed')
      .order('impression_at', { ascending: true });

    if (error || !rows) return;

    const total = rows.length;
    const rowsWithTime = rows.filter(r => r.time_on_screen_seconds > 0);
    const totalTime = rowsWithTime.reduce((a, r) => a + r.time_on_screen_seconds, 0);
    const avgTime = rowsWithTime.length > 0 ? totalTime / rowsWithTime.length : 0;

    const hourBuckets = getLast12HourBuckets();
    const byHour = {};
    rows.forEach(r => {
      if (!r.impression_at) return;
      const d = new Date(r.impression_at);
      const key = bucketKey(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours())));
      if (hourBuckets.some(h => bucketKey(h) === key)) {
        byHour[key] = (byHour[key] || 0) + 1;
      }
    });

    const weeklyRows = rows.filter(r => isThisWeek(r.impression_at));
    const weeklyEngagements = weeklyRows.filter(r => r.feedback !== null).length;
    const weeklyClicks = weeklyRows.filter(r => r.clicked).length;

    const byDevice = {};
    rows.forEach(r => { const d = r.device_type || 'unknown'; byDevice[d] = (byDevice[d] || 0) + 1; });

    const byCity = {};
    rows.forEach(r => { if (r.city) byCity[r.city] = (byCity[r.city] || 0) + 1; });

    const byTitle = {};
    rows.forEach(r => {
      const t = r.demographic_data?.current_title;
      if (t) byTitle[t] = (byTitle[t] || 0) + 1;
    });

    setStats({ total, totalTime, avgTime, byHour, hourBuckets, weeklyEngagements, weeklyClicks, byDevice, byCity, byTitle });
    setLastUpdated(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const deviceEntries = stats ? Object.entries(stats.byDevice).sort((a, b) => b[1] - a[1]) : [];
  const topCities = stats ? Object.entries(stats.byCity).sort((a, b) => b[1] - a[1]).slice(0, 6) : [];
  const topTitles = stats ? Object.entries(stats.byTitle).sort((a, b) => b[1] - a[1]).slice(0, 8) : [];

  const chartVals = stats ? stats.hourBuckets.map(h => stats.byHour[bucketKey(h)] || 0) : [];
  const chartLabels = stats ? stats.hourBuckets.map(h => fmtHourLocal(h)) : [];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f5f7',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif",
      color: '#1c1c1e',
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        padding: '0 32px',
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <a href="https://enoki.media/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <div style={{ background: '#FFE566', borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: '#5a4a00', cursor: 'pointer' }}>
              ENOKI
            </div>
          </a>
          <span style={{ color: '#c7c7cc', fontSize: 16 }}>×</span>
          <a href="https://www.landeed.com?utm_source=fig&utm_medium=chat" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#2D8C4E' }}>Landeed</span>
          </a>
          <div style={{ width: 1, height: 16, background: '#e5e5ea' }} />
          <span style={{ fontSize: 13, color: '#8E8E93' }}>Campaign Analytics</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#34C759', boxShadow: '0 0 0 2.5px rgba(52,199,89,0.25)' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#34C759', letterSpacing: 0.5 }}>LIVE</span>
          </div>
          {lastUpdated && (
            <span style={{ fontSize: 12, color: '#aeaeb2' }}>
              Updated {lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '70vh', flexDirection: 'column', gap: 12 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #e5e5ea', borderTopColor: '#2D8C4E', animation: 'spin 0.8s linear infinite' }} />
          <span style={{ fontSize: 14, color: '#8E8E93' }}>Loading analytics...</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : (
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '28px 24px 48px' }}>

          {/* KPI row */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
            <StatCard label="Total Impressions" value={stats.total.toLocaleString()} sub="India users reached" />
            <StatCard
              label="Total Time Watched"
              value={fmtTime(stats.totalTime)}
              sub="cumulative across all users"
              color="#2D8C4E"
            />
            <StatCard
              label="Avg. Watch Time"
              value={fmtTime(stats.avgTime)}
              sub="per person"
              color="#F39C12"
            />
          </div>

          {/* Chart + Budget side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 14, marginBottom: 20 }}>
            <Card title="Impressions — Last 12 Hours">
              <LineChart vals={chartVals} labels={chartLabels} />
            </Card>
            <BudgetCard weeklyEngagements={stats.weeklyEngagements} weeklyClicks={stats.weeklyClicks} />
          </div>

          {/* 2-col row — Device + Top Cities (India-specific) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
            <Card title="Device Type">
              {deviceEntries.length > 0 ? deviceEntries.map(([dev, count]) => (
                <HBar key={dev} label={dev.charAt(0).toUpperCase() + dev.slice(1)} value={count} max={deviceEntries[0][1]} color="#2D8C4E" pct />
              )) : <div style={{ color: '#8E8E93', fontSize: 13 }}>Accumulating...</div>}
            </Card>
            <Card title="Top Cities">
              {topCities.length > 0 ? topCities.map(([city, count]) => (
                <HBar key={city} label={city} value={count} max={topCities[0][1]} color="#F39C12" hideValue />
              )) : <div style={{ color: '#8E8E93', fontSize: 13 }}>Accumulating...</div>}
            </Card>
          </div>

          {/* Job titles full width */}
          <Card title="Top Job Titles Reached">
            {topTitles.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
                {topTitles.map(([title, count]) => (
                  <HBar key={title} label={title} value={count} max={topTitles[0][1]} color="#2D8C4E" pct />
                ))}
              </div>
            ) : (
              <div style={{ color: '#8E8E93', fontSize: 13 }}>Accumulating...</div>
            )}
          </Card>

          <div style={{ textAlign: 'center', marginTop: 40, fontSize: 12, color: '#aeaeb2' }}>
            Powered by{' '}
            <a href="https://enoki.media/" target="_blank" rel="noopener noreferrer" style={{ color: '#5a4a00', fontWeight: 700, textDecoration: 'none' }}>
              ENOKI
            </a>
            {' '}· refreshes every 30s
          </div>
        </div>
      )}
    </div>
  );
};

export default LandeedDashboard;
